import React from "react";
import { FlatList, View, Pressable } from "react-native";
import CCText from "@/Atoms/CCText";
import { ClubMemberRow } from "@/Hooks/ClubsApi/types";
import useLoggedInUserStore from "@/Store/loggedInUserstore";

const AVATAR_COLORS = ["#3DAB9E", "#E17846", "#67B3E1", "#937ADB", "#D1AB41"];
const avatarColor = (name: string) =>
  AVATAR_COLORS[Math.abs(name.charCodeAt(0)) % AVATAR_COLORS.length];

function MemberRow({ item, variant, onCompare, currentUserId }: { item: ClubMemberRow; variant: "mobile" | "rail"; onCompare?: (userId: number) => void; currentUserId: string }) {
  const isAdmin = item.role === "admin";
  const isRail = variant === "rail";
  const avatarSize = isRail ? 30 : 38;
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: isRail ? 9 : 11,
        paddingVertical: isRail ? 6 : 9,
        paddingHorizontal: 4,
        // Mobile rows are divided; rail rows sit flush in the card.
        borderBottomWidth: isRail ? 0 : 1,
        borderBottomColor: "rgba(38,35,34,0.10)",
      }}
    >
      {/* Avatar */}
      <View
        style={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarSize / 2,
          backgroundColor: avatarColor(item.username),
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CCText style={{ color: "#fff", fontWeight: "600", fontSize: isRail ? 11 : 13 }}>
          {item.username.charAt(0).toUpperCase()}
        </CCText>
      </View>

      {/* Name + role tag, plus optional "Active X" sub-line */}
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: isRail ? 5 : 6 }}>
          <CCText
            numberOfLines={1}
            style={{ fontSize: isRail ? 13 : 14.5, fontWeight: "600", color: "#262322", flexShrink: 1 }}
          >
            {item.name || item.username}
          </CCText>
          <View
            style={{
              backgroundColor: isAdmin ? "#E4F6EB" : "#F1F1F1",
              borderRadius: 6,
              paddingHorizontal: 7,
              paddingVertical: 2,
            }}
          >
            <CCText
              style={{
                fontSize: 10,
                fontWeight: isAdmin ? "700" : "600",
                letterSpacing: isAdmin ? 0.03 : 0,
                color: isAdmin ? "#1E7A3D" : "#888280",
              }}
            >
              {isAdmin ? "Admin" : "Member"}
            </CCText>
          </View>
        </View>
        {item.name ? (
          <CCText
            numberOfLines={1}
            style={{ fontSize: isRail ? 10.5 : 11.5, color: "#888280", marginTop: 1 }}
          >
            {item.username}
          </CCText>
        ) : null}
        {item.active ? (
          <CCText style={{ fontSize: isRail ? 10.5 : 11.5, color: "#888280", marginTop: 1 }}>
            Active {item.active}
          </CCText>
        ) : null}
      </View>

      {/* Compare button (right) — only when onCompare callback is provided */}
      {onCompare && String(item.user_id) !== String(currentUserId) ? (
        <View style={{ marginLeft: item.points != null ? 0 : "auto" }}>
          <Pressable
            onPress={() => onCompare(item.user_id)}
            style={{ paddingHorizontal: 8, paddingVertical: 4 }}
          >
            <CCText style={{ fontSize: 13, color: "#3DAB9E", fontWeight: "600" }}>
              Compare
            </CCText>
          </Pressable>
        </View>
      ) : item.points != null ? (
        <View style={{ alignItems: "flex-end" }}>
          <CCText style={{ fontSize: isRail ? 12.5 : 14, fontWeight: "700", color: "#4D3F37" }}>
            {item.points}
          </CCText>
          {!isRail ? (
            <CCText style={{ fontSize: 10, color: "#A8A7A6" }}>this week</CCText>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

export default function MembersTab({
  members,
  variant = "mobile",
  onCompare,
}: {
  members: ClubMemberRow[];
  variant?: "mobile" | "rail";
  onCompare?: (userId: number) => void;
}) {
  const currentUserId = useLoggedInUserStore((state) => state.userId);

  // Rail renders a plain column (it lives inside a scrolling card); mobile scrolls itself.
  if (variant === "rail") {
    return (
      <View>
        {members.map((m) => (
          <MemberRow
            key={String(m.user_id)}
            item={m}
            variant="rail"
            onCompare={onCompare}
            currentUserId={currentUserId}
          />
        ))}
      </View>
    );
  }
  return (
    <FlatList
      data={members}
      keyExtractor={(m) => String(m.user_id)}
      contentContainerStyle={{ paddingHorizontal: 14, paddingTop: 4, paddingBottom: 24 }}
      renderItem={({ item }) => (
        <MemberRow
          item={item}
          variant="mobile"
          onCompare={onCompare}
          currentUserId={currentUserId}
        />
      )}
    />
  );
}
